/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   collectionIntegrityApi.ts
 *     Feature         :   265 - Collection Integrity · US5
 *     Description     :   Read client for the collection-integrity endpoints.
 *
 *     🛑 Uses the authenticated `apiClient`, never a bare `fetch`. A raw fetch carries no
 *        bearer token, so every call 401s under the /api auth gate — the exact defect that
 *        broke the Manager dashboard in Spec 259.
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-31
 * =========================================================================================
 */

import { apiClient } from './apiClient';
import type {
  CleanStreak,
  CollectionProblem,
  StageCompleteness,
} from '../components/operations/CollectionIntegrityPanel';

export interface AbnormalTermination {
  id: string;
  runId: string;
  stage: string | null;
  state: string;
  inferredCause: string;
  causeConfidence: string;
  elapsedSeconds: number | null;
  detectedAt: string;
}

export interface CoverageAssessment {
  assessedAt: string;
  oracleState: string;
  reason: string | null;
  billedResourceCount: number | null;
  candidateCount: number | null;
}

export interface GapCandidate {
  id: string;
  resourceGuid: string;
  subscriptionId: string | null;
  resourceName: string | null;
  serviceName: string | null;
  observedCost: number | null;
  classification: string;
  acceptedBy: string | null;
}

/** Spec 286 T159 item 29: the open problems' true counts; `problems` holds the first 100 only. */
export interface ProblemCounts {
  total: number;
  permission: number;
  stages: number;
}

export interface CollectionJobsIntegrity {
  completeness: StageCompleteness[];
  problems: CollectionProblem[];
  problemCounts?: ProblemCounts;
  terminations: AbnormalTermination[];
  streaks: CleanStreak[];
}

export interface InfraSyncIntegrity {
  completeness: StageCompleteness[];
  allStages: StageCompleteness[];
  problems: CollectionProblem[];
  problemCounts?: ProblemCounts;
  /** 🛑 null means NEVER ASSESSED — render that differently from "assessed, none found". */
  assessment: CoverageAssessment | null;
  candidates: GapCandidate[];
}

export const collectionIntegrityApi = {
  async getCollectionJobs(): Promise<CollectionJobsIntegrity> {
    const r = await apiClient.get<{ success: boolean; data: CollectionJobsIntegrity }>(
      '/api/collection-integrity/collection-jobs',
    );
    return r.data.data;
  },

  async getInfrastructureSync(): Promise<InfraSyncIntegrity> {
    const r = await apiClient.get<{ success: boolean; data: InfraSyncIntegrity }>(
      '/api/collection-integrity/infrastructure-sync',
    );
    return r.data.data;
  },
};

export default collectionIntegrityApi;
